import { useEffect, useState, ChangeEvent, useLayoutEffect, useRef, KeyboardEvent, useMemo } from "react";
import { useBreadcrumbs } from "./BreadcrumbContext.tsx";
import { useAuth } from "../utils/AuthContext.tsx";
import { Link, useNavigate, useParams } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarHeader,
  SidebarMenu,
  SidebarGroupLabel,
  SidebarMenuItem,
  SidebarMenuButton,
  SidebarMenuAction,
} from "@/components/ui/sidebar";
import { Button } from "./ui/button.tsx";
import { CircleAlert, SquarePen, Terminal, Trash } from "lucide-react";
import { Conversation, Message } from "@/utils/Types.ts";
import remarkGfm from "remark-gfm";
import { Skeleton } from "./ui/skeleton.tsx";
import { Textarea } from "./ui/textarea.tsx";
import {
  useConversations,
  useCreateConversation,
  useDeleteConversation,
  useMessages,
  useCreateMessage,
} from "@/utils/Api.ts";
import { useQueryClient } from "@tanstack/react-query";

enum AgentResponseState {
  Requesting = "requesting",
  Starting = "starting",
  Pending = "pending",
  Data = "data",
  Completed = "completed",
}

interface AgentResponse {
  id: string | null;
  state: AgentResponseState;
  content: any;
  error: boolean;
}

function Assistant() {
  const { token, currentAccount } = useAuth();
  const { updateBreadcrumbs } = useBreadcrumbs();
  const navigate = useNavigate();
  const { conversationId } = useParams();
  const [selectedConversationId, setSelectedConversationId] = useState<string | undefined>(conversationId);
  const [messageInputValue, setMessageInputValue] = useState<string>("");
  const [currentResponse, setCurrentResponse] = useState<AgentResponse | undefined>();

  const {
    data: conversations,
    isFetching: isLoadingConversations,
    isError: isErrorLoadingConversations,
    error: errorLoadingConversations,
  } = useConversations(currentAccount?.id!, "user", "assistant", token ?? undefined);

  const {
    data: messages,
    isFetching: isLoadingMessages,
    isError: isErrorLoadingMessages,
    error: errorLoadingMessages,
  } = useMessages(selectedConversationId, token ?? undefined);

  const queryClient = useQueryClient();

  const {
    mutateAsync: createConversation,
    error: createConversationError,
    isPending: isCreatingConversation,
  } = useCreateConversation(currentAccount?.id, "user", "assistant", token ?? undefined);

  const {
    mutateAsync: createMessage,
    error: createMessageError,
    isPending: isCreatingMessage,
  } = useCreateMessage(token ?? undefined);

  const { mutateAsync: deleteConversation } = useDeleteConversation(
    currentAccount?.id,
    "user",
    "assistant",
    token ?? undefined
  );

  const sortedMessages = useMemo(() => {
    if (messages) {
      return messages
        .map((m: Message) => ({
          ...m,
          created: new Date(m.created as unknown as string),
          multiline: false,
        }))
        .sort((a, b) => a.created.getTime() - b.created.getTime());
    }
  }, [messages]);

  const lastMessageID = useMemo(() => {
    //iterate over sorted messages in reverse until we hit an agent message that represents the last response
    const temp = [...(sortedMessages ?? [])];
    while (temp.length > 0) {
      const message = temp.pop();
      if (message?.type === "agent") {
        return message.id;
      }
    }
  }, [sortedMessages]);

  const sortedConversations = useMemo(() => {
    if (conversations) {
      return conversations
        .map((c) => ({ ...c, updated: new Date(c.updated) }))
        .sort((a, b) => b.updated.getTime() - a.updated.getTime());
    }
  }, [conversations]);

  const initConversation = async (name: string): Promise<string | undefined> => {
    if (!selectedConversationId) {
      const created = await createConversation({ name });
      if (createConversationError) {
        console.error("Error creating conversation:", createConversationError);
        return;
      }

      setSelectedConversationId(created.id);
      navigate(`/assistant/${created.id}`);
      return created.id;
    } else {
      console.log(`Using existing conversation ${selectedConversationId}.`);
      return selectedConversationId;
    }
  };

  const addUserMessage = async (
    conversationId: string,
    previousId: string,
    content: string
  ): Promise<Message | undefined> => {
    console.debug(`Creating new user message for conversation ${conversationId}.`);

    let userMessage: Message = {
      id: "temp",
      conversation: conversationId,
      type: "agent",
      from: "user",
      previousId: previousId,
      content: content,
      sending: true,
      created: new Date(),
      attachments: 0,
      error: false,
      multiline: content.indexOf("\n") > 0,
    };

    const result = await createMessage(userMessage);
    if (createMessageError) {
      userMessage.error = true;
      return userMessage;
    }
    return result;
  };

  const addAssistantMessage = async (message: any) => {
    let assistantMessage: Message = {
      id: message.id,
      conversation: message.conversation,
      type: message.type,
      from: message.from,
      previousId: message.previousId,
      content: message.content,
      sending: false,
      created: new Date(),
      attachments: 0,
      error: false,
      multiline: true,
    };

    const key = ["messages", assistantMessage.conversation];
    await queryClient.cancelQueries({ queryKey: key });
    queryClient.setQueryData<Message[]>(key, (old = []) => [...old, assistantMessage]);
  };

  const updateContentCache = async (conversation: string, messageId: string, content: any) => {
    //update the query cache with the full content from the message
    const key = ["messages", conversation];
    await queryClient.cancelQueries({ queryKey: key });
    queryClient.setQueryData<Message[]>(key, (old = []) => {
      const newMessages = old.map((m) => {
        if (m.id == messageId) m.content = content;
        return m;
      });
      return newMessages;
    });
  };

  const startResponse = async (userMessage: Message) => {
    console.log(`Getting conversation stream for message ${userMessage.id}....`);
    const baseUrl = import.meta.env.VITE_CONSOLE_API_HOST ?? "";
    const endpoint = `${baseUrl}/api/messaging/responses/${userMessage.id}/sse?token=${token}`;
    const eventSource = new EventSource(endpoint);

    // Listen for incoming messages.
    eventSource.onmessage = (event: MessageEvent) => {
      try {
        const me = JSON.parse(event.data);
        if (!me) return;
        console.log(me);

        setCurrentResponse((prev) => {
          if (!prev) return prev;
          switch (me.type) {
            case "message.created": {
              addAssistantMessage(me.message);
              return {
                ...prev,
                id: me.message.id,
                state: AgentResponseState.Starting,
              };
            }
            case "response.created":
            case "response.in_progress":
              return {
                ...prev,
                state: AgentResponseState.Pending,
              };

            case "response.output_text.delta": {
              const existingText = prev.content?.[0]?.text ?? "";
              const updatedContent = [{ type: "output_text", text: existingText + me.delta }];
              return {
                ...prev,
                state: AgentResponseState.Data,
                content: updatedContent,
              };
            }

            case "message.completed": {
              updateContentCache(userMessage.conversation, prev.id!, me.content);
              return {
                ...prev,
                state: AgentResponseState.Completed,
                content: me.content,
              };
            }

            default:
              return prev;
          }
        });
      } catch (err) {
        console.error("Error parsing SSE message:", err);
      }
    };

    // Listen for errors in the connection.
    eventSource.onerror = (error) => {
      if (eventSource.readyState === EventSource.CLOSED) {
        console.log("Connection closed normally.");
      } else {
        console.error("Unexpected error:", error);
        eventSource.close();
      }
    };

    // Clean up the EventSource on component unmount.
    return () => {
      eventSource.close();
      console.debug("assistant response unmounted.");
    };
  };

  const submitRequest = async () => {
    try {
      const inputValue = messageInputValue;
      const conversationId = await initConversation(inputValue);
      if (!conversationId) throw new Error("Expected a conversation to be initialized, but it was not.");

      setCurrentResponse({
        id: null,
        state: AgentResponseState.Requesting,
        content: [],
        error: false,
      });

      //add a new message to the conversation
      const userMessage = await addUserMessage(conversationId, lastMessageID ?? "", inputValue);
      if (!userMessage || userMessage.error) throw new Error("Expected a user message, but got none.");

      //begins streaming a response to the user message
      startResponse(userMessage);
    } catch (err: any) {
      console.error("Error submitting user message:", err);
    } finally {
      setMessageInputValue("");
    }
  };

  const handleMessageInputKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    if (!event.shiftKey && event.code == "Enter") {
      submitRequest();
    }
  };

  // Update state whenever the input field changes.
  const handleMessageInputChange = (event: ChangeEvent<HTMLTextAreaElement>) => {
    setMessageInputValue(event.target.value);
  };

  const handleAsk = async () => {
    submitRequest();
  };

  const handleDelete = async (conversation: Conversation) => {
    await deleteConversation({ conversationId: conversation.id });
    if (selectedConversationId && conversation.id == selectedConversationId) {
      setCurrentResponse(undefined);
      setSelectedConversationId(undefined);
      navigate("/assistant");
    }
  };

  const handleConverationSelection = (conversation: Conversation) => {
    setSelectedConversationId(conversation.id);
  };

  const handleNew = () => {
    setCurrentResponse(undefined);
    setSelectedConversationId(undefined);
    navigate("/assistant");
  };

  useEffect(() => {
    updateBreadcrumbs([]);
  }, []);

  // run *after* every render where `messages` changes
  const bottomRef = useRef<HTMLDivElement | null>(null);
  useLayoutEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  return (
    <div className="flex w-full h-full">
      <Sidebar
        collapsible="none"
        className="hidden flex-1 md:flex w-[324px] md:left-12 pl-8 pr-8 pt-2 top-0 bottom-0 min-w-[324px] flex-grow-0 gap-8 fixed"
      >
        <SidebarHeader className="gap-3.5 p-4">
          <div className="text-base text-foreground">
            <div className="flex w-full pt-4 pr-6">
              <Link to={`/assistant`} onClick={handleNew}>
                <Terminal />
              </Link>
            </div>
            <h2 className="mt-10 scroll-m-20 pb-2 text-3xl font-semibold tracking-tight transition-colors first:mt-0 text-accent-foreground">
              Assistant
            </h2>
            <p className="text-xl text-muted-foreground">Ask your Field Agent for assistance</p>
          </div>
        </SidebarHeader>
        <SidebarContent>
          {(isLoadingConversations || conversations?.length) && (
            <SidebarGroup>
              <SidebarGroupLabel>History</SidebarGroupLabel>
              <SidebarGroupContent>
                <SidebarMenu>
                  {isErrorLoadingConversations && (
                    <span className="text-red-500">{errorLoadingConversations.message}</span>
                  )}
                  {isLoadingConversations && (
                    <div className="flex flex-col gap-2 px-2 py-2">
                      <Skeleton className="h-4 w-full" />
                      <Skeleton className="h-4 w-1/2" />
                      <Skeleton className="h-4 w-2/3" />
                      <Skeleton className="h-4 w-1/2" />
                    </div>
                  )}
                  {sortedConversations &&
                    sortedConversations.map((c: Conversation) => (
                      <SidebarMenuItem key={c.name}>
                        <SidebarMenuButton
                          asChild
                          isActive={c.id === selectedConversationId}
                          onClick={() => handleConverationSelection(c)}
                        >
                          <Link to={`/assistant/${c.id}`}>
                            <span>{c.name}</span>
                          </Link>
                        </SidebarMenuButton>
                        <SidebarMenuAction showOnHover onClick={() => handleDelete(c)}>
                          <Trash />
                        </SidebarMenuAction>
                      </SidebarMenuItem>
                    ))}
                </SidebarMenu>
              </SidebarGroupContent>
            </SidebarGroup>
          )}
        </SidebarContent>
      </Sidebar>
      {/* container for chat interface */}
      <div className="flex justify-center bg-muted fixed top-0 bottom-0 right-0 md:left-[372px] pl-8 overflow-y-scroll">
        <Button className="fixed right-8 top-8" variant="outline" size="icon" onClick={handleNew}>
          <SquarePen />
        </Button>
        {/* container for chat conversation */}
        <div className="max-w-[840px] w-full py-16 px-8">
          {/* container for messages */}
          <div className="flex flex-col gap-2 py-4 pb-[300px] items-end">
            {isLoadingMessages && (
              <>
                {/* container for loading elements */}
                <div className="flex flex-col gap-2 w-full py-12 items-end">
                  <Skeleton className="w-2/3 h-4" />
                  <Skeleton className="w-1/4 h-4" />
                </div>
                <div className="flex flex-col gap-2 w-full py-12">
                  <Skeleton className="w-2/3 h-4" />
                  <Skeleton className="w-full h-4" />
                  <Skeleton className="w-1/3 h-4" />
                  <Skeleton className="w-2/3 h-4" />
                  <Skeleton className="w-1/4 h-4" />
                </div>
              </>
            )}

            {isErrorLoadingMessages && <span className="text-red-500">{errorLoadingMessages.message}</span>}
            {selectedConversationId &&
              sortedMessages &&
              sortedMessages.map((m: Message, i: number) => {
                if (m.from === "user") {
                  return (
                    <UserMessage
                      key={i}
                      id={m.id}
                      content={m.content}
                      sending={m.sending}
                      error={m.error}
                      debug={false}
                    />
                  );
                } else if (m.from == "agent") {
                  const isCurrent = currentResponse && m.id === currentResponse.id;
                  return (
                    <AssistantMessage
                      key={i}
                      id={m.id}
                      content={isCurrent ? currentResponse.content : m.content ?? "isnull"}
                      state={isCurrent ? currentResponse.state : AgentResponseState.Completed}
                      error={m.error}
                      debug={false}
                    />
                  );
                } else {
                  return <div key={i}>Unknown Message Type</div>;
                }
              })}
            {currentResponse && currentResponse.state == AgentResponseState.Requesting && (
              <div className="px-4 py-8 w-full">
                <Skeleton className="w-1/4 h-4" />
              </div>
            )}
            {/*bottom marker for autoscroll */}
            <div className="pt-100px" ref={bottomRef} />
          </div>
          {/* container for chat input */}
          <div className="sticky bg-card p-4 bottom-16 w-full shadow-lg rounded-lg">
            <div className="flex flex-col gap-3">
              <div className="text-red-300">
                {createConversationError?.message}
                {createMessageError?.message}
              </div>
              <div className="flex gap-3">
                <Textarea
                  placeholder="What do you need help with?"
                  className="md:text-lg md:py-2"
                  onChange={handleMessageInputChange}
                  onKeyDown={handleMessageInputKeyDown}
                  disabled={isCreatingConversation || isCreatingMessage}
                  value={messageInputValue}
                />
                <Button size="lg" onClick={handleAsk} disabled={isCreatingConversation || isCreatingMessage}>
                  Ask
                </Button>
              </div>
            </div>
            {selectedConversationId && (
              <div className="px-6 py-2 text-xs text-slate-400">
                or, start a{" "}
                <Link to="/assistant" onClick={handleNew} className="hover:underline">
                  new conversation
                </Link>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function getMarkdown(content: any) {
  let elements: { markdown: string; annotations: any } = { markdown: "", annotations: [] };
  for (const c of content) {
    if (c.type == "output_text") {
      elements.markdown += c.text;
      elements.annotations = c.annotations ?? [];
    }
  }
  return elements;
}

const UserMessage: React.FC<{ id: string | null; sending: boolean; content: any; error: any; debug: boolean }> = ({
  id,
  sending,
  content,
  error,
  debug,
}) => {
  return (
    <div
      className={`py-4 px-6 max-w-[90%] rounded-lg ${
        sending ? "text-accent-foreground border border-accent-foreground" : "bg-accent text-accent-foreground"
      }`}
    >
      {error && <CircleAlert className="text-red-400 w-6 h-6 pr-2" />}
      <ReactMarkdown children={content} remarkPlugins={[remarkGfm]} />
      {debug && <div className="text-xs text-muted-foreground">id:{id}</div>}
    </div>
  );
};

const AssistantMessage: React.FC<{
  id: string | null;
  state: AgentResponseState;
  content: any;
  error: any;
  debug: boolean;
}> = ({ id, state, content, error, debug }) => {
  let child = null;
  if (state == AgentResponseState.Starting) {
    return (child = <Skeleton className="w-1/4 h-4" />);
  } else if (state == AgentResponseState.Pending) {
    child = <Skeleton className="w-1/2 h-4" />;
  } else if (state == AgentResponseState.Data || state == AgentResponseState.Completed) {
    const elements = getMarkdown(content);
    child = (
      <>
        {error && <CircleAlert className="text-red-400 w-6 h-6 pr-2" />}
        <ReactMarkdown children={elements.markdown} remarkPlugins={[remarkGfm]} />
        <ul>
          {elements.annotations.map((a: any, i: number) => (
            <li className="text-xs font-mono text-muted-foreground" key={i}>
              [source: "{a.filename}"]
            </li>
          ))}
        </ul>
      </>
    );
  }
  return (
    <div className="px-4 py-8 w-full markdown-container">
      {child}
      {debug && <div className="text-xs text-muted-foreground">id:{id}</div>}
    </div>
  );
};

export default Assistant;
